<template>
  <td
    :style="containerStyle"
    :class="containerClass"
    @click="onClick"
    @keydown="onKeyDown"
    role="cell"
    :data-prime="editingRowData"
  >
    <!-- {{column.$scopedSlots}} -->
    <span v-if="responsiveLayout === 'stack'" class="p-column-title">{{
      columnProp("header")
    }}</span>
    <ColumnSlot
      :data="rowData"
      :column="column"
      :field="field"
      :index="rowIndex"
      type="body"
      :frozenRow="frozenRow"
      :editorInitCallback="editorInitCallback"
      v-if="column.$scopedSlots.body && !d_editing"
    />
    <!-- <ColumnSlot :data="rowData" :column="column" :field="field" :index="rowIndex" :type="field+'Body'" :frozenRow="frozenRow" :editorInitCallback="editorInitCallback" v-if="column.$scopedSlots.hasOwnProperty(field+'Body')  && !d_editing" /> -->
    <ColumnSlot
      :data="editingRowData"
      :column="column"
      :field="field"
      :index="rowIndex"
      type="editor"
      :frozenRow="frozenRow"
      :editorSaveCallback="editorSaveCallback"
      :editorCancelCallback="editorCancelCallback"
      v-else-if="column.$scopedSlots.editor && d_editing"
    />
    <ColumnSlot
      :data="editingRowData"
      :column="column"
      :field="field"
      :index="rowIndex"
      type="body"
      :frozenRow="frozenRow"
      :editorSaveCallback="editorSaveCallback"
      :editorCancelCallback="editorCancelCallback"
      v-else-if="!column.$scopedSlots.editor && column.$scopedSlots.body && d_editing"
    />
    <template v-else-if="columnProp('selectionMode')">
      <DTRadioButton
        :value="rowData"
        :checked="selected"
        @change="toggleRowWithRadio($event, rowIndex)"
        v-if="columnProp('selectionMode') === 'single'"
      />
      <DTCheckbox
        :value="rowData"
        :checked="selected"
        @change="toggleRowWithCheckbox($event, rowIndex)"
        v-else-if="columnProp('selectionMode') === 'multiple'"
      />
    </template>
    <template v-else-if="columnProp('rowReorder')">
      <i
        :class="['p-datatable-reorderablerow-handle', columnProp('rowReorderIcon') || 'pi pi-bars']"
      ></i>
    </template>
    <template v-else-if="columnProp('expander')">
      <button class="p-row-toggler p-link" @click="toggleRow" type="button" v-ripple>
        <span :class="rowTogglerIcon"></span>
      </button>
    </template>
    <template v-else-if="editMode === 'row' && columnProp('rowEditor')">
      <button
        class="p-row-editor-init p-link"
        v-if="!d_editing"
        @click="onRowEditInit"
        type="button"
        v-ripple
      >
        <span class="p-row-editor-init-icon pi pi-fw pi-pencil"></span>
      </button>
      <button
        class="p-row-editor-save p-link"
        v-if="d_editing"
        @click="onRowEditSave"
        type="button"
        v-ripple
      >
        <span class="p-row-editor-save-icon pi pi-fw pi-check"></span>
      </button>
      <button
        class="p-row-editor-cancel p-link"
        v-if="d_editing"
        @click="onRowEditCancel"
        type="button"
        v-ripple
      >
        <span class="p-row-editor-cancel-icon pi pi-fw pi-times"></span>
      </button>
    </template>
    <template v-else>{{ resolveFieldData() }}</template>
  </td>
</template>

<script>
import OverlayEventBus from "/node_modules/primevue/overlayeventbus/OverlayEventBus";
import DomHandler from "/node_modules/primevue/utils/DomHandler";
import ObjectUtils from "/node_modules/primevue/utils/ObjectUtils";
import ColumnSlot from "./ColumnSlot.vue";
import RowRadioButton from "./RowRadioButton";
import RowCheckbox from "./RowCheckbox.vue";
import Ripple from "/node_modules/primevue/ripple/Ripple";

export default {
  props: {
    rowData: {
      type: Object,
      default: null,
    },
    column: {
      type: Object,
      default: null,
    },
    frozenRow: {
      type: Boolean,
      default: false,
    },
    rowIndex: {
      type: Number,
      default: null,
    },
    index: {
      type: Number,
      default: null,
    },
    rowTogglerIcon: {
      type: Array,
      default: null,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    editing: {
      type: Boolean,
      default: false,
    },
    editingMeta: {
      type: Object,
      default: null,
    },
    editMode: {
      type: String,
      default: null,
    },
    responsiveLayout: {
      type: String,
      default: "stack",
    },
  },
  documentEditListener: null,
  selfClick: false,
  data() {
    return {
      d_editing: this.editing,
      styleObject: {
        left: "",
        right: "",
      },
    };
  },
  watch: {
    editing(newValue) {
      this.d_editing = newValue;
    },
    "$data.d_editing": function (newValue) {
      this.$emit("editing-meta-change", {
        data: this.rowData,
        field: this.field || `field_${this.index}`,
        index: this.rowIndex,
        editing: newValue,
      });
    },
  },
  mounted() {
    if (this.columnProp("frozen")) {
      this.updateStickyPosition();
    }
  },
  updated() {
    if (this.columnProp("frozen")) {
      this.updateStickyPosition();
    }

    if (
      this.d_editing &&
      (this.editMode === "cell" || (this.editMode === "row" && this.columnProp("rowEditor")))
    ) {
      const focusableEl = DomHandler.getFirstFocusableElement(this.$el);
      focusableEl && focusableEl.focus();
    }
  },
  beforeDestroy() {
    if (this.overlayEventListener) {
      OverlayEventBus.off("overlay-click", this.overlayEventListener);
      this.overlayEventListener = null;
    }
  },
  methods: {
    columnProp(prop) {
      return ObjectUtils.getVNodeProp(this.column, prop);
    },
    resolveFieldData() {
      return ObjectUtils.resolveFieldData(this.rowData, this.field);
    },
    toggleRow(event) {
      this.$emit("row-toggle", {
        originalEvent: event,
        data: this.rowData,
      });
    },
    toggleRowWithRadio(event, index) {
      this.$emit("radio-change", {
        originalEvent: event.originalEvent,
        index: index,
        data: event.data,
      });
    },
    toggleRowWithCheckbox(event, index) {
      this.$emit("checkbox-change", {
        originalEvent: event.originalEvent,
        index: index,
        data: event.data,
      });
    },
    isEditable() {
      return this.column.$scopedSlots.editor != null;
    },
    bindDocumentEditListener() {
      if (!this.documentEditListener) {
        this.documentEditListener = (event) => {
          if (!this.selfClick) {
            this.completeEdit(event, "outside");
          }
          this.selfClick = false;
        };

        document.addEventListener("click", this.documentEditListener);
      }
    },
    unbindDocumentEditListener() {
      if (this.documentEditListener) {
        document.removeEventListener("click", this.documentEditListener);
        this.documentEditListener = null;
        this.selfClick = false;
      }
    },
    switchCellToViewMode() {
      this.d_editing = false;
      this.unbindDocumentEditListener();
      OverlayEventBus.off("overlay-click", this.overlayEventListener);
      this.overlayEventListener = null;
    },
    onClick(event) {
      if (this.editMode === "cell" && this.isEditable()) {
        this.selfClick = true;

        if (!this.d_editing) {
          this.d_editing = true;
          this.bindDocumentEditListener();
          this.$emit("cell-edit-init", {
            originalEvent: event,
            data: this.rowData,
            field: this.field,
            index: this.rowIndex,
          });

          this.overlayEventListener = (e) => {
            if (this.$el && this.$el.contains(e.target)) {
              this.selfClick = true;
            }
          };
          OverlayEventBus.on("overlay-click", this.overlayEventListener);
        }
      }
    },
    completeEdit(event, type) {
      const completeEvent = {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        value: this.rowData[this.field],
        newValue: this.editingRowData[this.field],
        field: this.field,
        index: this.rowIndex,
        type: type,
        defaultPrevented: false,
        preventDefault: function () {
          this.defaultPrevented = true;
        },
      };

      this.$emit("cell-edit-complete", completeEvent);

      if (!completeEvent.defaultPrevented) {
        this.switchCellToViewMode();
      }
    },
    onKeyDown(event) {
      if (this.editMode === "cell") {
        switch (event.which) {
          case 13:
            this.completeEdit(event, "enter");
            break;

          case 27:
            this.switchCellToViewMode();
            this.$emit("cell-edit-cancel", {
              originalEvent: event,
              data: this.rowData,
              field: this.field,
              index: this.rowIndex,
            });
            break;

          case 9:
            this.completeEdit(event, "tab");

            if (event.shiftKey) this.moveToPreviousCell(event);
            else this.moveToNextCell(event);
            break;
        }
      }
    },
    moveToPreviousCell(event) {
      let currentCell = this.findCell(event.target);
      let targetCell = this.findPreviousEditableColumn(currentCell);

      if (targetCell) {
        DomHandler.invokeElementMethod(targetCell, "click");
        event.preventDefault();
      }
    },
    moveToNextCell(event) {
      let currentCell = this.findCell(event.target);
      let targetCell = this.findNextEditableColumn(currentCell);

      if (targetCell) {
        DomHandler.invokeElementMethod(targetCell, "click");
        event.preventDefault();
      }
    },
    findCell(element) {
      if (element) {
        let cell = element;
        while (cell && !DomHandler.hasClass(cell, "p-cell-editing")) {
          cell = cell.parentElement;
        }

        return cell;
      } else {
        return null;
      }
    },
    findPreviousEditableColumn(cell) {
      let prevCell = cell.previousElementSibling;

      if (!prevCell) {
        let previousRow = cell.parentElement.previousElementSibling;
        if (previousRow) {
          prevCell = previousRow.lastElementChild;
        }
      }

      if (prevCell) {
        if (DomHandler.hasClass(prevCell, "p-editable-column")) return prevCell;
        else return this.findPreviousEditableColumn(prevCell);
      } else {
        return null;
      }
    },
    findNextEditableColumn(cell) {
      let nextCell = cell.nextElementSibling;

      if (!nextCell) {
        let nextRow = cell.parentElement.nextElementSibling;
        if (nextRow) {
          nextCell = nextRow.firstElementChild;
        }
      }

      if (nextCell) {
        if (DomHandler.hasClass(nextCell, "p-editable-column")) return nextCell;
        else return this.findNextEditableColumn(nextCell);
      } else {
        return null;
      }
    },
    isEditingCellValid() {
      return DomHandler.find(this.$el, ".p-invalid").length === 0;
    },
    onRowEditInit(event) {
      this.$emit("row-edit-init", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    onRowEditSave(event) {
      this.$emit("row-edit-save", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    onRowEditCancel(event) {
      this.$emit("row-edit-cancel", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    editorInitCallback(event) {
      this.$emit("row-edit-init", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    editorSaveCallback(event) {
      this.$emit("row-edit-save", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    editorCancelCallback(event) {
      this.$emit("row-edit-cancel", {
        originalEvent: event,
        data: this.rowData,
        newData: this.editingRowData,
        field: this.field,
        index: this.rowIndex,
      });
    },
    updateStickyPosition() {
      if (this.columnProp("frozen")) {
        let align = this.columnProp("alignFrozen");
        if (align === "right") {
          let right = 0;
          let next = this.$el.nextElementSibling;
          if (next) {
            right = DomHandler.getOuterWidth(next) + parseFloat(next.style.right || 0);
          }
          this.styleObject.right = right + "px";
        } else {
          let left = 0;
          let prev = this.$el.previousElementSibling;
          if (prev) {
            left = DomHandler.getOuterWidth(prev) + parseFloat(prev.style.left || 0);
          }
          this.styleObject.left = left + "px";
        }
      }
    },
  },
  computed: {
    editingRowData() {
      return this.editingMeta[this.rowIndex] ? this.editingMeta[this.rowIndex].data : this.rowData;
    },
    field() {
      return this.columnProp("field");
    },
    containerClass() {
      return [
        this.columnProp("bodyClass"),
        this.columnProp("className"),
        {
          "p-selection-column": this.columnProp("selectionMode") != null,
          "p-editable-column": this.isEditable(),
          "p-cell-editing": this.d_editing,
          "p-frozen-column": this.columnProp("frozen"),
        },
      ];
    },
    containerStyle() {
      let bodyStyle = this.columnProp("bodyStyle");
      let columnStyle = this.columnProp("styles");

      return this.columnProp("frozen")
        ? [columnStyle, bodyStyle, this.styleObject]
        : [columnStyle, bodyStyle];
    },
  },
  components: {
    ColumnSlot: ColumnSlot,
    DTRadioButton: RowRadioButton,
    DTCheckbox: RowCheckbox,
  },
  directives: {
    ripple: Ripple,
  },
};
</script>
